import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import { Loader } from '@systemeio/ui-frontend'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import { Comment } from 'modules/comments/components/comment'
import { CommentSortSelector } from 'modules/comments/components/comment-sort-selector'
import type { CommentInterface } from 'modules/comments/components/comment/comment.types'
import { CommentListWithLoader } from 'modules/comments/components/comments-list/comment-list-with-loader'
import type { CommentsContextType } from 'modules/comments/components/comments-list/comments-list.types'
import { MessageBox } from 'modules/comments/components/message-box/message-box'
import { StickyMessageBox } from 'modules/comments/components/message-box/sticky-message-box'
import { StickyMessageBoxWithReply } from 'modules/comments/components/message-box/sticky-message-box-with-reply'
import {
  CommentHighlightProvider,
  useCommentHighlight,
} from 'modules/comments/contexts/comment-highlight'
import { useCommentsWithCacheManagement } from 'modules/comments/hooks/use-comments-with-cache-management'
import type { CommentSortEnum, CommentsTypeEnum, OnErrorType } from 'modules/comments/types'
import { saveCommentSortPreference } from 'modules/comments/utils/comment-preferences'
import type { CommentsHighlightContext } from 'modules/community/components/post/types'
import { CommentsListSkeleton } from './comments-list-skeleton'
import { PinnedCommentsList } from './pinned-comments-list'

export const CommentsContext = createContext<CommentsContextType | undefined>(undefined)

export function useCommentsContext(): CommentsContextType {
  const context = useContext(CommentsContext)

  if (!context) {
    throw new Error('useCommentsContext must be used within a CommentsContextProvider')
  }

  return context
}

interface BaseCommentListProps {
  commentsType: CommentsTypeEnum
  shouldFetch?: boolean
  readOnly?: boolean
  onError?: OnErrorType
  targetCommentId?: number
  commentsHighlightContext?: CommentsHighlightContext
}
type CommentsCourseAndCommunityProps =
  | {
      commentsType: CommentsTypeEnum.Community
      community: {
        communityPath: string
        postId: number
      }
    }
  | {
      commentsType: CommentsTypeEnum.Page
      course: {
        pageId: number
      }
    }
  | {
      commentsType: CommentsTypeEnum.LightLecture
      lightLecture: {
        lectureId: number
      }
    }
export type CommentsProps = BaseCommentListProps & CommentsCourseAndCommunityProps

const CommentsListInner = (props: CommentsProps): ReactNode => {
  const {
    commentsType,
    shouldFetch = true,
    readOnly = false,
    onError,
    targetCommentId,
    commentsHighlightContext,
  }: CommentsProps = props

  const { t } = useTranslation()
  const { setHighlightedComment, highlightedComment } = useCommentHighlight()

  const isCommunityProps = 'community' in props
  const isLightLectureProps = 'lightLecture' in props
  const resourceId = isCommunityProps
    ? props.community.postId
    : isLightLectureProps
      ? props.lightLecture.lectureId
      : props.course.pageId

  const [activeComment, setActiveComment] = useState<CommentInterface | null>(null)
  const [pinnedComments, setPinnedComments] = useState<CommentInterface[]>([])

  const {
    comments,
    replies,
    sortOption,
    updateReplies,
    handleSortChange: originalHandleSortChange,
    handleLoadMoreGap,
    getPageCommentsQuery,
    setComments,
    isReloading,
  } = useCommentsWithCacheManagement({
    commentsType,
    pageId: resourceId,
    shouldFetch,
    onError: error => {
      if (onError) onError(error)
      Sentry.captureException(error, {
        extra: { context: 'Comments error' },
      })
    },
    targetCommentId,
    isCommunityComments: isCommunityProps,
    setHighlightedComment,
    highlightedComment,
  })

  const handleSortChange = (SortOption: CommentSortEnum): void => {
    saveCommentSortPreference(SortOption)
    originalHandleSortChange(SortOption)
  }

  const isReplyMessageBox = activeComment !== null
  const isAllowCommentingAndNotReplyMessageBox = !isReplyMessageBox && !readOnly

  const showInitialSkeletonLoading = getPageCommentsQuery.isValidating && comments.length === 0

  useEffect(() => {
    if (!activeComment) return
    const handleEscKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        setActiveComment(null)
      }
    }
    document.addEventListener('keydown', handleEscKey)
    return () => document.removeEventListener('keydown', handleEscKey)
  }, [activeComment])

  useEffect(() => {
    if (commentsHighlightContext?.targetCommentId && !!comments?.length) {
      const commentElement = document.querySelector<HTMLDivElement>(
        `#comment-${commentsHighlightContext?.targetCommentId}`,
      )
      const container = document.querySelector('[data-scrollable="comments-container"]')
      if (commentElement && container) {
        container.scrollTo({ top: commentElement.offsetTop, behavior: 'smooth' })
      }
    }
  }, [commentsHighlightContext?.targetCommentId, !!comments?.length])

  const replyInfo =
    !!activeComment && isCommunityProps
      ? {
          userId: activeComment.userId,
          displayName: activeComment.userName,
          profileImage: activeComment.userAvatarUrl,
        }
      : undefined

  const extraProviderValue = isCommunityProps
    ? {
        community: {
          communityPath: props.community.communityPath,
          postId: props.community.postId,
        },
      }
    : isLightLectureProps
      ? {
          lightLecture: {
            lectureId: props.lightLecture.lectureId,
          },
        }
      : {
          course: {
            pageId: props.course.pageId,
          },
        }

  const activeCommentParentId =
    (!Array.isArray(activeComment?.parent) ? activeComment?.parent.id : activeComment?.id) || null

  return (
    <CommentsContext.Provider
      value={{
        commentsType,
        commentsList: comments,
        setComments,
        repliesList: replies,
        updateReplies,
        pinnedComments,
        setPinnedComments,
        readOnly,
        extra: extraProviderValue,
        currentSortOption: sortOption,
      }}
    >
      <div className="relative">
        <div className="flex flex-col items-stretch space-y-5 md:space-y-6">
          {(comments.length > 0 || pinnedComments.length > 0) && (
            <CommentSortSelector
              currentSort={sortOption}
              onSortChange={handleSortChange}
              disabled={getPageCommentsQuery.isLoading}
            />
          )}

          {!isCommunityProps && isAllowCommentingAndNotReplyMessageBox && (
            <MessageBox parentId={null} onError={onError} />
          )}

          {isCommunityProps && (
            <PinnedCommentsList
              postId={props.community.postId}
              setActiveComment={setActiveComment}
              activeComment={activeComment}
              onError={onError}
              targetCommentId={targetCommentId}
            />
          )}

          {showInitialSkeletonLoading ? (
            <CommentsListSkeleton />
          ) : (
            <div className="relative">
              {isReloading && (
                <div
                  className="absolute inset-0 z-10 flex items-start justify-center pt-20"
                  role="status"
                  aria-live="polite"
                  aria-label="Reloading comments"
                >
                  <Loader />
                </div>
              )}

              <div
                className={twMerge(
                  'space-y-5 transition-all duration-300 md:space-y-6',
                  isReloading && 'shimmer-loading pointer-events-none opacity-50 select-none',
                )}
                inert={isReloading ? true : undefined}
              >
                <CommentListWithLoader
                  items={comments}
                  renderItem={comment => {
                    const highlighted = commentsHighlightContext?.searchHighlightedComments?.find(
                      h => h.id === comment.id,
                    )
                    return (
                      <Comment
                        key={comment.id}
                        data={{ ...(highlighted ?? {}), ...comment }}
                        level={0}
                        setActiveComment={setActiveComment}
                        activeComment={activeComment}
                        onError={onError}
                        replies={replies[comment.id]?.replies}
                        loadMoreReplies={handleLoadMoreGap}
                      />
                    )
                  }}
                  onLoadMore={handleLoadMoreGap}
                  parentId={null}
                  loadMoreLabel={t('comments.components.comments.load_more_label')}
                />
              </div>
            </div>
          )}
        </div>

        {/* NOTE: It's a workaround to have the comment field re-created due to React life-cycle. Since Slate is uncontrollable and we can only use initValue on mount component */}
        {!isCommunityProps ? null : isAllowCommentingAndNotReplyMessageBox ? (
          <StickyMessageBox parentId={null} onError={onError} />
        ) : (
          <StickyMessageBoxWithReply
            parentId={activeCommentParentId}
            setActiveComment={setActiveComment}
            onError={onError}
            replyInfo={replyInfo}
            setResponses={(parentId, reply) =>
              updateReplies({
                commentId: parentId,
                replies: isCommunityProps
                  ? [...(replies[parentId]?.replies ?? []), reply]
                  : [reply, ...(replies[parentId]?.replies ?? [])],
              })
            }
          />
        )}
      </div>
    </CommentsContext.Provider>
  )
}

export const CommentsList = (props: CommentsProps): ReactNode => {
  return (
    <CommentHighlightProvider>
      <CommentsListInner {...props} />
    </CommentHighlightProvider>
  )
}
